import { Fragment, useState, useEffect } from "react";

import {
  CalendarIcon,
  MapPinIcon as LocationMarkerIcon,
  UsersIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid";
import { useRouter } from "next/router";
import Link from "next/link";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

import { getAllPursuits, getSettingsForPage } from "../lib/api";
import BlockContent from "../components/block-content";
import Meta from "../components/meta";
import ImageFeature from "../components/image-feature";
import Modal from "../components/modal";

const emptyState = (
  <div className="border border-gray-200 rounded-lg p-6 flex flex-col items-center">
    <div className="max-w-sm flex flex-col items-center">
      <svg
        className="h-8 w-8 text-gray-500 text-center"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>

      <h3 className="mt-2 text-sm font-medium text-gray-900">
        No Active Pursuits
      </h3>
      <p className="mt-1 text-sm text-gray-500 text-center">
        We do not have any pursuits open for bidding, check back here in the
        future for updates.
      </p>
    </div>
  </div>
);

const PursuitDetails = ({ data = {}, open, setOpen, router }) => {
  return (
    <Modal
      open={open}
      setOpen={(value) => {
        if (!value) {
          setTimeout(() => {
            router.push("/pursuits", undefined, {
              scroll: false,
              shallow: true,
            });
          }, 300);
        }
        setOpen(value);
      }}
    >
      <div className="relative w-full">
        {data && data.title ? (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                <span className="sr-only">Details for </span>
                {data.title}
              </h2>
              <p className="text-sm font-medium text-gray-500">
                {data.location}
              </p>
            </div>
            <div>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200">
                <div className="py-3 text-sm font-medium">
                  <div className="flex justify-between max-w-md">
                    <dt className="text-gray-500">Estimator</dt>
                    <dd className="text-gray-900 text-right">
                      {data.estimatorName}
                      <br />
                      <a
                        className="text-gray-500 hover:text-accent font-normal"
                        href={`mailto:${data.estimatorEmail}`}
                      >
                        {data.estimatorEmail}
                      </a>
                    </dd>
                  </div>
                </div>

                {data.coordinatorName ? (
                  <div className="py-3 text-sm font-medium">
                    <div className="flex justify-between max-w-md">
                      <dt className="text-gray-500">Coordinator</dt>
                      <dd className="text-gray-900 text-right">
                        {data.coordinatorName}
                        <br />
                        <a
                          className="text-gray-500 hover:text-accent font-normal"
                          href={`mailto:${data.estimatorEmail}`}
                        >
                          {data.coordinatorEmail}
                        </a>
                      </dd>
                    </div>
                  </div>
                ) : null}

                <div className="py-3 text-sm font-medium">
                  <div className="flex justify-between max-w-md">
                    <dt className="text-gray-500">Bid Date</dt>
                    <dd className="text-gray-900">
                      {dayjs(data.bidDate).format("MMM Do YYYY h:mma")}
                    </dd>
                  </div>
                </div>
                <div className="py-3 text-sm font-medium items-center">
                  <div className="flex justify-between max-w-md">
                    <dt className="text-gray-500">Bid Documents</dt>
                    <dd>
                      <a
                        target="_blank"
                        href={data.documents}
                        rel="noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                      >
                        View Documents
                      </a>
                    </dd>
                  </div>
                </div>
              </dl>
            </div>

            <div>
              <BlockContent
                className="text-gray-500 prose-sm prose max-w-none"
                content={data.description}
              />
            </div>

            <div className="flex mt-6">
              <a
                target="_blank"
                href={data.documents}
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                View Bid Documents
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default function Pursuits({ data, settings }) {
  const router = useRouter();
  const { query } = router || {};
  const { pursuit: pursuitId } = query || {};

  const [open, setOpen] = useState(!!pursuitId);
  const hasData = data && data.length;
  let pursuit;
  if (pursuitId && hasData) {
    pursuit = data.find((i) => i["_id"] === pursuitId);
  }

  useEffect(() => {
    if (!open && pursuitId) {
      setOpen(true);
    } else if (open && !pursuitId) {
      setOpen(false);
    }
  }, [pursuitId]);

  return (
    <>
      <Meta path="/pursuits" {...settings.seo} />

      <div className="relative bg-white">
        <div className="bg-gradient-to-b from-accent-50 via-white to-white h-[700px] absolute top-0 left-0 right-0 z-0 pointer-events-none"></div>

        <div className="relative mx-auto px-4 max-w-7xl sm:px-6 lg:px-8">
          <div className="pt-10 mb-12">
            <h1 className="text-3xl sm:text-4xl font-extrabold sm:tracking-tight text-gray-900">
              {settings.title}
            </h1>
            <div className="mt-6">
              <BlockContent
                className="space-y-6 text-xl text-gray-500 block-content"
                content={settings.content}
              />
            </div>
          </div>

          {hasData ? (
            <div className="bg-white border-gray-200 border overflow-hidden sm:rounded-md">
              <ul role="list" className="divide-y divide-gray-200">
                {data.map((pursuit) => {
                  let staff = pursuit.estimatorName;
                  if (
                    pursuit.coordinatorName &&
                    pursuit.estimatorName !== pursuit.coordinatorName
                  ) {
                    staff = `${staff} & ${pursuit.coordinatorName}`;
                  }

                  return (
                    <li key={pursuit["_id"]}>
                      <Link
                        href={`/pursuits?pursuit=${pursuit["_id"]}`}
                        shallow
                        scroll={false}
                        className="block hover:bg-gray-50"
                      >
                        <div className="flex items-center px-4 py-4 sm:px-6 justify-between">
                          <div className="min-w-0 max-w-2xl flex-shrink-0 w-full flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-accent-600">
                                {pursuit.title}
                              </p>
                            </div>

                            <div className="mt-2 sm:flex sm:justify-between">
                              <div className="sm:flex">
                                <p className="flex items-center text-sm text-gray-500">
                                  <UsersIcon
                                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                  {staff}
                                </p>
                                <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                  <LocationMarkerIcon
                                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                  {pursuit.location}
                                </p>

                                <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                  <CalendarIcon
                                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                  Closing on{" "}
                                  {dayjs(pursuit.bidDate).format(
                                    "MMMM Do YYYY [at] h:mma"
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <ChevronRightIcon
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            emptyState
          )}

          <PursuitDetails
            data={pursuit}
            router={router}
            setOpen={setOpen}
            open={open}
          />
        </div>

        {settings.features?.length > 0 ? (
          <div className="relative pt-20 pb-32 overflow-hidden space-y-24">
            {settings.features?.map((feature, index) => (
              <ImageFeature key={feature._key} index={index} data={feature} />
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

export async function getStaticProps({ preview = false }) {
  const data = await getAllPursuits(preview);
  const settings = await getSettingsForPage("pursuits");

  return {
    props: { data, settings },
    revalidate: 10,
  };
}
